<template>
  <div
    ref="svg"
    style="position: relative;"
    v-bind:class="['root', 'geo-chart-'+ numerator + '-' + denominator, {'col_1_2': expanded || $mq === 'sm' || printing}]"
  >
    <a
      v-on:click.prevent="setIndicator([numerator, denominator])"
      v-bind:href="$router.resolve({ query: {...$route.query, shortcut: url} } ).href"
      class="clickCursor">
      <font-awesome-icon v-if="isSelectedShortcut" icon="map-marked" title="Currently mapped"/>
      {{name}}
    </a>

    <div class="contextGeog">&nbsp;<span v-if="regionData">Southeast Michigan: {{format((regionData[numerator] / regionData[denominator]) || 0)}}</span></div>
    <div class="contextGeog">&nbsp;<span v-if="countyData && countyData.name">{{countyData.name}}: {{format((countyData[numerator] / countyData[denominator]) || 0)}}</span></div>
    <div class="contextGeog" v-if="largeChart" style="font-size: 75%;">&nbsp;
      <span v-if="selected">
        <strong
          v-bind:class="{ 'selected-bar': selected.geoid === selectedId, 'hover-bar': selected.geoid === hoverId }"
        >{{selected.name}}
        </strong>: <span v-html="tooltipFormat(selected)"></span>
      </span>
    </div>
    <div class="mapboxgl-ctrl-top-right">
      <div class="mapboxgl-ctrl mapboxgl-ctrl-group">
        <button
          v-bind:class="['mapboxgl-ctrl-icon', {'mapboxgl-ctrl-shrink': expanded, 'mapboxgl-ctrl-fullscreen': !expanded}]"
          v-on:click="expanded = !expanded"
        ><span class="mapboxgl-ctrl-icon" aria-hidden="true"></span></button>
      </div>
      <div class="mapboxgl-ctrl mapboxgl-ctrl-group" v-bind:title="'Download CSV for: ' + name" v-on:click="saveFile()">
        <font-awesome-icon class="save-file" icon="file-download"/>
      </div>
    </div>
    <svg
      v-on:mouseleave="setHover(null)"
      v-bind:viewBox='"0,0,"+(width + margin.left + margin.right)+","+(height + margin.top + margin.bottom)'
      tabindex="0"
      v-on:keyup.left="shift(-1)"
      v-on:keyup.right="shift(1)"
      v-on:keyup.enter="setSelected(hoverId)"
    >
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#6f978d;stop-opacity:.1"/>
          <stop offset="100%" style="stop-color:#6f978d;stop-opacity:.9"/>
        </linearGradient>

        <linearGradient
          v-for="color in usedColorLegend"
          v-bind:key="color"
          v-bind:id="'grad' + color.replace(/[(),#\s]+/g, '-')"
          x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"
                v-bind:style="{'stop-color':color, 'stop-opacity':.1}"
          />
          <stop offset="100%"
                v-bind:style="{'stop-color':color, 'stop-opacity':.7}"
          />
        </linearGradient>
      </defs>
      <g
        v-bind:transform="'translate(' + (width - 10) + ',' + margin.top + ')'"
        class="moeLegend"
        v-if="hasMoe"
      >
        <Bar
          v-bind:height="30"
          v-bind:width="10"
          v-bind:step="0"
          v-bind:x="0"
          v-bind:y="[legendScale('Estimate - Margin of Error'), legendScale('Estimate'), legendScale('Estimate + Margin of Error')]"
        />
        <yAxis v-bind:scale="legendScale" v-bind:axisWidth="-2" style="font-size: 0.45em;"/>
      </g>
      <g
        v-bind:transform="'translate(' + margin.left + ',' + margin.top + ')'"
        v-if="printing || visible"
      >
        <yAxis v-bind:scale="yScale" v-bind:format="format" v-bind:axisWidth="yAxisWidth - 1 - ((xScale.step() - xScale.bandwidth()) / 2)"/>
        <g v-bind:transform="'translate(' + yAxisWidth + ',0)'">
          <polygon
            points="-2.5,-5 0,0 2.5,-5"
            v-if="largeChart && selected"
            v-bind:class="{ 'selected-bar': selected.geoid === selectedId, 'hover-bar': selected.geoid === hoverId }"
            v-bind:transform="'translate(' + (xScale(selected.geoid) + (xScale.bandwidth() / 2))  + ',' + yScale(selected.value[2]) + ')'"
          />
          <Bar
            v-for="datum in geogData"
            v-bind:key="datum.geoid"
            v-bind:class="['g' + datum.geoid]"
            v-on:click="setSelected(datum.geoid)"
            v-on:mouseenter="setHover(datum.geoid)"
            v-on:mouseleave="setHover(null)"
            v-bind:tooltip="largeChart ? {}: {
                        content: '<div>' + datum.name + '</div><div>' + tooltipFormat(datum) + '</div>',
                        classes: ['d3-tip'],
                        trigger: 'manual',
                        container: $refs.svg,
                        placement: 'top',
                        show: (datum.geoid === (hoverId || selectedId)),
                        boundariesElement: $refs.svg,
                      }"
            v-bind:height="height"
            v-bind:width="xScale.bandwidth() + (!smallChart && !largeChart && (datum.geoid === hoverId || datum.geoid === selectedId) ? xScale.bandwidth(): 0)"
            v-bind:step="xScale.step() + (!smallChart && !largeChart && (datum.geoid === hoverId || datum.geoid === selectedId) ? xScale.bandwidth(): 0)"
            v-bind:x="xScale(datum.geoid) + bigHoverBar(datum.geoid)"
            v-bind:y="[yScale(datum.value[0]), yScale(datum.value[1]), yScale(datum.value[2])]"
            v-bind:clamped="clamped < datum.value[2]"
            v-bind:selected="datum.geoid === selectedId"
            v-bind:hover="datum.geoid === hoverId"
            v-bind:fill="colorLegend(datum.value[1])"
            fill-opacity="0.8"
          />
          <text
            v-if="clamped"
            text-anchor="end"
            y="-6"
            x="-2"
            style="font-size: 10px;"
          >{{format(this.geogData[0].value[1])}} ^</text>
          <text
            v-if="clamped"
            x="-3"
            fill="white"
            style="font-weight: bold; font-size: large; text-shadow: 0 -2px grey; pointer-events: none;"
          >~</text>
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import yAxis from './yAxis.vue'
import Bar from './Bar'
import debounce from 'lodash.debounce'
import * as d3 from 'd3'
import scrollMonitor from 'scrollmonitor'
import { saveAs } from 'file-saver';
import Vue from 'vue'

export default {
  name: 'GioDistributionChart',
  components: { Bar, yAxis },
  props: [
    'name',
    'url',
    'numerator',
    'denominator',
    'D3FormatterString',
    'selectedId',
    'hoverId',
    'geogData',
    'isSelectedShortcut',
    'colorLegendData',
    'countyData',
    'regionData',
    'hideWhenInvisible',
    'printing',
  ],
  data: function () {
    return {
      margin: { top: 15, right: 10, bottom: 10, left: 10 },
      width: 500,
      height: 140,
      yAxisWidth: 49,
      visible: !this.hideWhenInvisible,
      expanded: this.isSelectedShortcut,
    }
  },
  mounted: function () {
    if (this.hideWhenInvisible) {
      let profile = document.getElementById('charts')
      let containerMonitor = scrollMonitor.createContainer(profile)
      let elementWatcherProfile = containerMonitor.create(this.$refs.svg)
      let elementWatcher = scrollMonitor.create(this.$refs.svg)
      let callBack = debounce(() => {
        if (this.$mq === 'sm') {
          this.visible = elementWatcher.isInViewport
        } else {
          this.visible = elementWatcherProfile.isInViewport
        }
      }, 400)
      elementWatcher.visibilityChange(callBack)
      elementWatcherProfile.visibilityChange(callBack)
      callBack()
      this.$once('hook:beforeDestroy', function () {
        elementWatcher.destroy()
        elementWatcherProfile.destroy()
        containerMonitor.destroy()
      })
    }
  },
  methods: {
    setIndicator: function (id) {
      this.$emit('set-map', id)
    },
    setSelected: function (id) {
      this.$emit('set-sel-geom-id', id)
    },
    setHover: debounce(function (id) {
      if (this.hoverId !== id) {
        this.$emit('set-hov-geom-id', id)
      }
    }, 150),
    shift: function (offset) {
      if (!(this.hoverId || this.selectedId)) {
        return
      }
      let index = this.geogData.findIndex(datum => datum.geoid === (this.hoverId || this.selectedId)) + offset
      if (index < 0 || index >= this.geogData.length) {
        return
      }
      this.setHover(this.geogData[index].geoid)
    },
    bigHoverBar: function (geoid) {
      if (this.largeChart || this.smallChart) {
        return 0
      }
      if (this.hoverId && this.selectedId && this.hoverId === this.selectedId) {
        if (this.xScale(geoid) > this.xScale(this.hoverId)) {
          return this.xScale.bandwidth()
        } else {
          return 0
        }
      }
      let out = 0
      if (this.hoverId && this.xScale(geoid) > this.xScale(this.hoverId)) {
        out += 1
      }
      if (this.selectedId && this.xScale(geoid) > this.xScale(this.selectedId)) {
        out += 1
      }
      return this.xScale.bandwidth() * out
    },
    colorLegend: function (value) {
      if (!this.colorLegendData) {
        return
      }
      for (let leg of this.colorLegendData) {
        if (value < leg.key[1]) {
          return leg.value
        }
      }
    },
    tooltipFormat: function (datum) {
      let delta = datum.value[2] - datum.value[0]
      if (delta === 0) {
        return '<strong>' + this.format(datum.value[1]) + '</strong>'
      } else {
        return '<strong>' + this.format(datum.value[1]) + '</strong><small> +/- ' + this.format(delta / 2) + '</small>'
      }
    },
    saveFile: function () {
      // eslint-disable-next-line no-undef
      ga('send', 'event', 'DownloadCSV', this.name, 'Community Explorer')
      // eslint-disable-next-line no-undef
      gtag('event', "DownloadCSV", {'change': this.name, 'map_name': 'Community Explorer'});
      let blob = new Blob([
        'id,name,', this.name, ',moe\n',
        this.geogData.map(d => [d.geoid, d.name, d.value[1], (d.value[2] - d.value[0]) / 2].join(',')).join('\n'),
      ], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, 'SEMCOG CommunityExplorer ' + this.name + ' 2020-2024 ACS_Data.csv')
    },
  },
  computed: {
    largeChart: function () {
      return this.printing || this.geogData.length > 300
    },
    smallChart: function () {
      return this.geogData.length < 50
    },
    clamped: function () {
      let cut = Math.ceil(this.geogData.length / 300)
      if (!this.smallChart && this.geogData && this.geogData[cut] && (0.75 * this.geogData[0].value[2] > this.geogData[cut].value[2])) {
        return this.geogData[cut].value[2]
      } else {
        return undefined
      }
    },
    legendScale: function () {
      let x = d3.scaleOrdinal().range([30, 20, 10, 0])
      x.domain(['0', 'Estimate - Margin of Error', 'Estimate', 'Estimate + Margin of Error'])
      return x
    },
    xScale: function () {
      let len = this.geogData.length
      let x = d3.scaleBand().padding(0.25).range([0, (this.width - this.yAxisWidth) * len / (len + 2)])
      x.domain(this.geogData.map(d => d.geoid))
      return x
    },
    hasMoe: function () {
      return this.geogData.some(d => d.value[0] !== d.value[2])
    },
    yScale: function () {
      let y = d3.scaleLinear().range([this.height, 0])
      if (this.geogData && this.geogData[0]) {
        if (this.clamped) {
          y.domain([0, this.clamped]).clamp(true)
        } else {
          y.domain([0, this.geogData[0].value[2]])
        }
      }
      return y
    },
    format: function () {
      return d3.format(this.D3FormatterString || '.0%')
    },
    usedColorLegend: function () {
      if (!this.colorLegendData) {
        return []
      }
      return Array.from(new Set(this.geogData.map(datum => this.colorLegend(datum.value[1])))).filter(c => c)
    },
    selected: function () {
      return this.geogData.find(datum => datum.geoid === (this.hoverId || this.selectedId))
    },
  },
  watch: {
    isSelectedShortcut: function () {
      if (this.isSelectedShortcut) {
        this.expanded = true
        Vue.nextTick(() => {
          this.$el.scrollIntoView({ block: 'center' })
        })
      }
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only. /deep/ applies to children as well -->
<style scoped>
  svg {
    width: 100%;
  }

  .root {
    position: relative;
    background: white;
    padding: 5px;
    margin: 9px;
    border-radius: 5px;
    border-color: #cbc7c7;
    border-width: 1px;
    border-style: solid;
    box-shadow: 10px 20px 17px -8px #cbc7c7;
  }

  .info {
    content: '';
    cursor: pointer;
    position: absolute;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E %3Cpath fill='%23333333' fill-rule='evenodd' d='M4,10a6,6 0 1,0 12,0a6,6 0 1,0 -12,0 M9,7a1,1 0 1,0 2,0a1,1 0 1,0 -2,0 M9,10a1,1 0 1,1 2,0l0,3a1,1 0 1,1 -2,0'/%3E %3C/svg%3E");
    background-color: rgba(255, 255, 255, 0.5);
    width: 24px;
    height: 24px;
    box-sizing: border-box;
    border-radius: 12px;
  }

  .mapboxgl-ctrl-top-right{
    z-index: 1;
  }

  .save-file {
    width: 29px;
    height: 29px;
    padding: 2px;
  }

  /deep/ .hover-bar {
    fill: #22323b;
    color: #22323b;
    opacity: 1;
  }

  /deep/ .selected-bar {
    fill: #dc4915;
    color: #dc4915;
    opacity: 1;
  }

  h2, h3, h4, h5 {
    margin: 0;
  }

  a.clickCursor {
    font-size: 1.17em;
    color: #0f2d53;
    font-weight: bold;
    vertical-align: middle;
    /*text-decoration: none;*/
  }

  .contextGeog {
    font-size: 0.7rem;
    margin: 1px;
    padding: 1px;
    line-height: 1.25em;
  }

  svg:focus, h6:focus {
    outline: 0;
    border-radius: 4px;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }

  .moeLegend {
    display: none;
  }

  .col_1_2 .moeLegend {
    display: inherit;
  }

  @media (max-width: 576px), print {
    .mapboxgl-ctrl-icon {
      display: none;
    }

    .root {
      page-break-before: auto;
      page-break-inside: avoid;
    }
  }
</style>
